import { useEffect, useState } from 'react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { cn } from '@/lib/utils'
import { formatChatTime } from '@/constants/chat'
import { Loader2 } from 'lucide-react'
import { collection, query, where, onSnapshot } from 'firebase/firestore'
import { getFirebaseDb, isFirebaseAvailable } from '@/lib/firebaseInit'
import {
  deduplicateRoomsByParticipant,
  filterRoomsWithMessages,
  preserveUnreadCounts,
  sortContactsByTime,
  transformChatContactUserDetails,
} from '@/utils/chatUtils'
import { useUnreadMessages } from '@/hooks/useUnreadMessages'
import { CHATROOM_PATH } from '@/constants/chat'
import type { ChatContact } from '@/schemas/chat.schema'

interface ChatContactsListProps {
  currentUserId: string
  selectedContactId?: string
  onContactSelect: (contactId: string, roomId?: string) => void
}

export default function ChatContactsList({
  currentUserId,
  selectedContactId,
  onContactSelect,
}: Readonly<ChatContactsListProps>) {
  const [contacts, setContacts] = useState<ChatContact[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const { unreadByRoom } = useUnreadMessages(currentUserId)

  useEffect(() => {
    if (!currentUserId) return

    setLoading(true)
    setError(null)

    if (!isFirebaseAvailable()) {
      setError('Chat service is currently unavailable. Please try again later.')
      setLoading(false)
      return
    }

    const db = getFirebaseDb()
    if (!db) {
      setError('Chat service is currently unavailable. Please try again later.')
      setLoading(false)
      return
    }

    const chatRoomsRef = collection(db, CHATROOM_PATH)
    const firebaseQuery = query(
      chatRoomsRef,
      where('participants', 'array-contains', currentUserId)
    )

    const unsubscribe = onSnapshot(
      firebaseQuery,
      async snapshot => {
        const rooms: Record<string, unknown>[] = []
        snapshot.forEach(doc => {
          rooms.push({
            id: doc.id,
            ...doc.data(),
          })
        })
        const uniqueRooms = deduplicateRoomsByParticipant(rooms, currentUserId)
        const roomsWithMessages = filterRoomsWithMessages(uniqueRooms)
        const contactList: ChatContact[] = await Promise.all(
          roomsWithMessages.map(async (room: Record<string, unknown>) => {
            const contactData: ChatContact =
              await transformChatContactUserDetails(room, currentUserId)

            return contactData
          })
        )
        const sortedContacts = sortContactsByTime(contactList)
        setContacts(prevContacts =>
          preserveUnreadCounts(sortedContacts, prevContacts)
        )
        setLoading(false)
      },
      err => {
        console.error('Error fetching contacts:', err)
        setError('Failed to load contacts')
        setLoading(false)
      }
    )

    return () => unsubscribe()
  }, [currentUserId])

  useEffect(() => {
    if (Object.keys(unreadByRoom).length === 0) return

    setContacts(prevContacts =>
      prevContacts.map(contact => {
        if (!contact.roomId) return contact
        const unreadCount = unreadByRoom[contact.roomId]
        if (unreadCount !== undefined) {
          return {
            ...contact,
            unreadCount: unreadCount > 0 ? unreadCount : undefined,
          }
        }

        return contact
      })
    )
  }, [loading, unreadByRoom])

  if (loading) {
    return (
      <div className="w-80 border-r border-ss-neutral-50 bg-white flex items-center justify-center">
        <div className="flex flex-col items-center gap-2">
          <Loader2 className="h-6 w-6 animate-spin text-ss-blue-500" />
          <p className="text-sm text-ss-neutral-500">Loading contacts...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="w-80 border-r border-ss-neutral-50 bg-white flex items-center justify-center">
        <p className="text-sm text-destructive">{error}</p>
      </div>
    )
  }

  return (
    <div className="w-80 bg-white rounded-xl border border-ss-neutral-50 flex flex-col ml-4 mb-4">
      {/* Header */}
      <div className="p-4 border-b border-ss-neutral-50">
        <h2 className="text-lg font-semibold text-ss-neutral-950">Messages</h2>
        <p className="text-xs text-ss-neutral-500 mt-1">
          {contacts.length} conversation{contacts.length !== 1 ? 's' : ''}
        </p>
      </div>

      {/* Contacts List */}
      <div className="flex-1 overflow-y-auto">
        {contacts.length === 0 ? (
          <div className="flex items-center justify-center h-full p-8">
            <p className="text-sm text-ss-neutral-500 text-center">
              No conversations yet. Start chatting to see your contacts here.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-ss-neutral-50 overflow-y-auto h-[calc(100vh-220px)]">
            {contacts.map(contact => (
              <button
                key={contact.id}
                onClick={() => onContactSelect(contact.id, contact.roomId)}
                className={cn(
                  'w-full p-4 flex items-start gap-3 hover:bg-ss-neutral-25 transition-colors text-left',
                  selectedContactId === contact.id &&
                    'bg-ss-blue-50 hover:bg-ss-blue-50 '
                )}
              >
                <div className="relative">
                  <Avatar className="h-12 w-12">
                    <AvatarImage src={contact.avatar} alt={contact.name} />
                    <AvatarFallback className="bg-ss-neutral-100 text-ss-neutral-600 font-medium">
                      {contact.name
                        .split(' ')
                        .map(n => n[0])
                        .join('')}
                    </AvatarFallback>
                  </Avatar>
                  {contact.online && (
                    <div className="absolute bottom-0 right-0 h-3 w-3 bg-green-500 rounded-full border-2 border-white" />
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <h3
                      className={cn(
                        'font-semibold text-sm truncate text-ss-neutral-950'
                      )}
                    >
                      {contact.name}
                    </h3>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      {contact.lastMessageTime && (
                        <span
                          className={cn(
                            'text-xs',
                            contact.unreadCount && contact.unreadCount > 0
                              ? 'text-ss-neutral-950 font-semibold'
                              : 'text-ss-neutral-500'
                          )}
                        >
                          {formatChatTime(contact.lastMessageTime)}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    {contact.lastMessage && (
                      <p
                        className={cn(
                          'text-xs truncate flex-1',
                          contact.unreadCount && contact.unreadCount > 0
                            ? 'text-ss-neutral-950 font-semibold'
                            : 'text-ss-neutral-600'
                        )}
                      >
                        {contact.lastMessage}
                      </p>
                    )}
                    {contact.unreadCount && contact.unreadCount > 0 && (
                      <span className="flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[11px] font-bold text-white bg-ss-blue-500 rounded-full flex-shrink-0">
                        {contact.unreadCount > 99 ? '99+' : contact.unreadCount}
                      </span>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
